import { styled, alpha } from '@mui/material/styles'
import { AppBar, Toolbar, Box, Button, IconButton, Avatar } from '@mui/material'

export const StyledAppBar = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'scrolled',
})<{ scrolled?: boolean }>(({ theme, scrolled }) => {
    return {
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'none',
        transition: theme.transitions.create(
            ['background-color', 'box-shadow'],
            { duration: 850, easing: theme.transitions.easing.easeInOut },
        ),
        ...(scrolled && {
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.18)}`,
        }),
    }
})

export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    minHeight: 72,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    position: 'relative',

    [theme.breakpoints.down('sm')]: {
        minHeight: 64,
        height: 64,
        alignItems: 'center',
    },
}))


export const DesktopRight = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}))

export const MobileLeft = styled(Box)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('md')]: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
    },
}))

export const MobileCenter = styled(Box)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('md')]: {
        display: 'flex',
        justifyContent: 'center',
        flex: 1,
    },
}))

export const MobileRight = styled(Box)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('md')]: {
        display: 'flex',
        justifyContent: 'flex-end',
        flex: 1,
    },
}))

// Rendered as a real router link (component={RouterLink}) so right-click works
export const LogoWrapper = styled(Box)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    gap: theme.spacing(1),

    [theme.breakpoints.down('md')]: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        height: 40,
    },
}))


export const LogoBox = styled(Box)(({ theme }) => ({
    width: 32,
    height: 32,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    background: `linear-gradient(135deg, ${theme.palette.brand.main} 0%, #4A0E1F 100%)`,
}))

export const LogoWord = styled(Box)(({ theme }) => ({
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: '-0.5px',
    display: 'flex',
    alignItems: 'center'
}))

export const LogoAccent = styled(Box)(({ theme }) => ({
    color: theme.palette.brand.main,
}))

export const NavContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'dimmed',
})<{ dimmed?: boolean }>(({ theme, dimmed }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: '1 1 auto',
    justifyContent: 'center',
    opacity: dimmed ? 0.2 : 1,
    pointerEvents: dimmed ? 'none' : 'auto',
    transition: 'opacity 0.25s ease',
}))


// Rendered as a real router link (component={RouterLink}) so right-click works
export const NavButton = styled(Button)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontSize: 15,
    textShadow: `0 1px 3px ${alpha(theme.palette.background.paper, 0.9)}`,
    '&:hover': {
        color: theme.palette.text.primary,
    },
}))

export const RightContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginLeft: 'auto',

    [theme.breakpoints.down('md')]: {
        position: 'relative',
        zIndex: 3,
    },

    [theme.breakpoints.down('sm')]: {
        flex: '0 0 auto',
        alignItems: 'center',
        height: 48
    },
}));




export const SearchWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
}))

export const SearchIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
}))

export const SearchPanel = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '100%',
    right: 0,
    width: 280,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    zIndex: 10,
}))

export const SearchRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}))

export const SearchInput = styled('input')(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1),
    fontSize: 14,
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    outline: 'none',
    color: theme.palette.text.primary,
    background: theme.palette.background.paper,
}))

export const CloseButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
}))

export const ResultsBox = styled(Box)(({ theme }) => ({
    maxHeight: 280,
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
}))

export const ResultItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    fontSize: 14,
    cursor: 'pointer',
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}))

// Poster thumbnail shown to the left of each search result. Size matches the
// Randomizer's search dropdown for consistency.
export const SearchThumb = styled('img')(({ theme }) => ({
    width: 32,
    height: 48,
    objectFit: 'cover',
    borderRadius: Number(theme.shape.borderRadius),
    flexShrink: 0,
    backgroundColor: theme.palette.background.default,
}))

// Placeholder for results with no poster (people, or titles missing artwork).
export const SearchThumbFallback = styled(Box)(({ theme }) => ({
    width: 32,
    height: 48,
    borderRadius: Number(theme.shape.borderRadius),
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
}))

export const StatusRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    fontSize: 14,
    color: theme.palette.text.secondary,
}))

export const CorrectedRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    fontSize: 13,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}))

export const LeftContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    [theme.breakpoints.down('md')]: {
        minWidth: 0,
    },
}));

export const LogoText = styled(Box)(({ theme }) => ({
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: '-0.5px',
    display: 'flex',
    alignItems: 'center',
}))

export const UserAvatar = styled(Avatar)(({ theme }) => ({
    display: 'flex',
    width: 36,
    height: 36,
    backgroundColor: theme.palette.brand.main,
    color: '#fff',
    cursor: 'pointer',
}))

// Mobile-only hamburger area on the left of the toolbar
export const MobileMenuArea = styled(Box)(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        display: 'flex',
    },
}))

export const MenuIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.primary,
}))

// Mobile-only centered logo overlay; only the logo itself is clickable.
export const MobileLogoOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'none',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 2,
    [theme.breakpoints.down('md')]: {
        display: 'flex',
    },
}))

export const MobileLogoClickable = styled(Box)({
    pointerEvents: 'auto',
})

export const DesktopLeftContainer = styled(LeftContainer)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}))

export const DesktopNavContainer = styled(NavContainer)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        // Absolute-center to the toolbar so the nav sits in the true middle of
        // the bar, independent of the differing left (logo) and right
        // (search/sign-in) widths.
        display: 'flex',
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        flex: '0 0 auto',
    },
}))

export const DesktopSignInButton = styled(NavButton)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        display: 'flex',
    },
}))

// Groups the Sign In button with the "more" menu (About/Guide/Help) for
// logged-out desktop users; hidden below md where the mobile hamburger covers it.
export const DesktopLoggedOutArea = styled(Box)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}))

export const DesktopMoreMenuButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
}))
